import { format } from 'date-fns';

/**
 * A reconciliation window's ends are calendar days, not instants: the bank states a reference date
 * and the API passes it through as `2026-08-04`.
 *
 * `new Date('2026-08-04')` reads that as UTC midnight, and formatting the result in the browser's
 * timezone then renders the *previous* day for anyone west of UTC — the window would be reported as
 * a day earlier than the one the figures belong to. Building the date from its parts gives local
 * midnight instead, so the day formatted is the day given, wherever it is viewed.
 *
 * Same problem the booking dates have, one step earlier: there the API sends an instant and the fix
 * is to read its UTC day (see TransactionBatches/bookingDate.ts); here there is no instant to read,
 * so the answer is not to invent one.
 */
const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/;

export const formatReferenceDay = (day: string): string => {
  if (!CALENDAR_DAY.test(day)) {
    return day;
  }

  const [year, month, date] = day.split('-').map(Number);
  const parsed = new Date(year, month - 1, date);

  // A day that does not exist is not rejected, it is rolled forward: `new Date(2026, 1, 29)` is
  // 01 Mar 2026. Reading the parts back out is what tells a real date from a normalised one — and
  // it also catches the two-digit-year mapping, where `0026` would silently become 1926.
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== date
  ) {
    return day;
  }

  return format(parsed, 'dd MMM yyyy');
};
